import VRuntimeTemplate from "v-runtime-template";
import dayjs from "dayjs";

import { AD_LAYOUT_STUBS, AD_PLACEMENTS } from "@consts";
import {
	canonical,
	openExternalLinkInNewTab,
	forceHistoryPushOnAnchorElement,
	isInternalAnchorElement,
	findAnchorElementFromEvent,
} from "@utils";

import SocialSharing from "../blog/BlogArticle/SocialSharing.vue";
import { modifyWithNewsletterPlaceholderDiv } from "../blog/BlogArticle/helpers";
import { getCMSCopy } from "@/common/services/cmsCopyService";
import NewsletterBanner from "@/components/NewsletterBanner/NewsletterBanner.vue";

export default {
	name: "WordSearch",
	components: {
		SocialSharing,
		VRuntimeTemplate,
		NewsletterBanner,
	},
	head() {
		const h = {
			title: this.articleData?.metaTitle,
			meta: [
				{
					name: "description",
					hid: "description",
					content: this.articleData?.metaDescription,
				},
				{
					name: "og:title",
					content: this.articleData?.metaTitle,
				},
				{
					name: "og:description",
					content:
						this.articleData?.teaser ||
						this.articleData?.metaDescription,
				},
				{
					name: "og:image",
					content: this.article?.coverImage?.urlBase || "",
				},
				{
					name: "og:url",
					content: canonical(this.$route).href,
				},
				{
					name: "og:type",
					content: "article",
				},
				{
					name: "article:published_time",
					content: this.entryPublishedTime,
				},
				{
					hid: "max-image-preview",
					name: "robots",
					content: "max-image-preview:large",
				},
			],
			link: [canonical(this.$route)],
			script: [
				{
					hid: "jsonld-word-search-article-schema",
					type: "application/ld+json",
					json: {
						"@context": "https://schema.org",
						"@type": "NewsArticle",
						headline: this.articleData?.metaTitle,
						image: [this.articleData?.coverImage?.urlBase || ""],
						datePublished: dayjs
							.tz(this.articleData?.firstPublishedAt, "UTC")
							.toISOString(),
						dateModified: dayjs
							.tz(this.articleData?.updatedAt, "UTC")
							.toISOString(),
						author: [
							{
								"@type": "Person",
								name:
									this.articleData?.author?.displayName || "",
								url: "https://wordfinder.yourdictionary.com/about/",
							},
						],
					},
				},
			],
		};

		return h;
	},
	data() {
		return {
			AD_LAYOUT_STUBS,
			AD_PLACEMENTS,
			articleData: null,
		};
	},
	async fetch() {
		const cmsCopyKey = ".seo.landing.word-search";
		const cmsCopy = await getCMSCopy(
			this.$axios,
			this.$config,
			cmsCopyKey,
			process.env.version
		);

		this.articleData = cmsCopy.data;
	},
	computed: {
		pageMeta() {
			const strings = [];
			const articleAuthor = this.articleData?.author?.displayName || "";
			if (articleAuthor) {
				strings.push(articleAuthor);
			}

			try {
				const articleDate = this.articleData?.updatedAt || "";
				if (articleDate) {
					strings.push(
						new Date(articleDate).toLocaleDateString("en", {
							month: "short",
							day: "numeric",
							year: "numeric",
						})
					);
				}
				if (this.articleData?.minutesToRead) {
					strings.push(
						`${this.articleData?.minutesToRead} ${this.$t(
							"ArticlesIndexPageReadingTime"
						)}`
					);
				}
			} catch (e) {}

			return strings.join(" &bull; ");
		},
		articleBodyRuntimeTemplate() {
			const body = modifyWithNewsletterPlaceholderDiv(
				this.articleData?.body || ""
			);

			return `<div class="article__body" @click="onBodyCopyClick">${body}</div>`;
		},
	},
	methods: {
		onBodyCopyClick(e) {
			const anchorElement = findAnchorElementFromEvent(e);

			if (!anchorElement) {
				return;
			}

			e.preventDefault();

			if (isInternalAnchorElement(anchorElement)) {
				forceHistoryPushOnAnchorElement(
					anchorElement,
					this.$router.history
				);
				return;
			}

			openExternalLinkInNewTab(anchorElement, window);
		},
	},
};
